import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ScannerItem, ScannerSignalStats } from '../types';
import { sigLabel } from '../interpret';   // 📖 화면 이름 사전(TERMS.md) — 값은 그대로, 글자만 바꿈
import EarningsBadge from './EarningsBadge';

// 🎨 규격 V2(08-23) 부품 — 칩은 높이 20·반경 6·11px 하나로 통일. 색은 dc 토큰만(밝은 원색·이모지 아이콘 금지).
const CHIP = 'inline-flex items-center h-5 px-2 rounded-md border text-[11.5px] font-semibold whitespace-nowrap';
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const TONE = {
  point:  'border-dc-point/35 bg-dc-point/[0.08] text-dc-point',
  up:     'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  upSoft: 'border-dc-up/30 bg-dc-up/[0.06] text-dc-up',
  down:   'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
  warn:   'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
  strong: 'border-dc-line2 bg-dc-bg2 text-dc-ink1',
  mute:   'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  faint:  'border-dc-line1 bg-dc-base/70 text-dc-ink3',
} as const;

// 시그널 배지 — 강한 추세일수록 웜(상승색), 아직 모양만 잡힌 쪽은 중립, 힘 빠진 쪽은 흐림·주의색.
//   ⚠️ 초록(dc.ok)은 여기 안 쓴다 — 초록은 "확실히 좋음"(국면 양호·조건 통과·감지 정상)에만(규격 V2).
const SIGNAL_STYLE: Record<string, string> = {
  '신규진입':   TONE.mute,     // 화면 글자: 초기
  '완벽한추세': TONE.up,       // 매우 강함
  '강한추세':   TONE.upSoft,   // 강함
  '추세형성':   TONE.mute,     // 형성 중
  '단기추세':   TONE.mute,     // 짧음
  '단기관망':   TONE.faint,
  '관망':       TONE.faint,
  '위험':       TONE.warn,
  // 역추세 시그널
  '반등임박':   TONE.upSoft,
  '과매도':     TONE.down,
  '중립':       TONE.mute,
  '약세이탈':   TONE.warn,
};

// 점수 — 등급별 색의 '칩'으로(막대 대신). 한눈에 강도 구분 + 깔끔.
function ScoreBar({ score }: { score: number }) {
  const cls = score >= 70 ? TONE.strong : score >= 50 ? TONE.mute : TONE.faint;
  return (
    <span className={`inline-flex items-center justify-center min-w-[36px] px-2 py-1 rounded-md border text-sm font-bold tabular-nums ${cls}`}>
      {score}
    </span>
  );
}

// ① 점수 → '실적 있는 등급'으로 번역 + ② 오늘 급락 괴리 태그(점수는 어제 일봉 기준이라 후행).
//   이모지 등급 표시(💎🟢🟡⚪)는 08-23 은퇴 — **앰버 → 잉크 사다리**가 그 자리를 대신한다(초록 금지).
function gradeOf(s: number) {
  if (s >= 90) return { label: '최상급', band: '90+', cls: TONE.point };
  if (s >= 80) return { label: '강함', band: '80~89', cls: TONE.strong };
  if (s >= 70) return { label: '보통', band: '70~79', cls: TONE.mute };
  return { label: '약함', band: '<70', cls: TONE.faint };
}
function GradeCell({ score, chg, sigStats, met, failed }: { score: number; chg: number; sigStats?: ScannerSignalStats | null; met?: number; failed?: string[] }) {
  const g = gradeOf(score);
  const grp = sigStats?.by_score?.[g.band];
  let tip = `${g.band} 등급 (원점수 ${score}) — 종합점수는 매수 신호가 아니라 스크리닝 순위 도구`;
  // ⑤ Minervini 8조건 — 몇 개 충족했고 무엇이 빠졌나(점수의 근거).
  // conditions_failed는 정식 8조건 경로에서만 채워짐(단기/ETF 경로엔 없음) → 그 경우만 표기.
  if (failed != null) tip += `\n8조건 중 ${met ?? (8 - failed.length)}개 충족${failed.length ? ` · 미충족: ${failed.join(', ')}` : ' · 완전 충족'}`;
  if (grp) {
    const h = ['5', '3', '2', '1'].find((k) => (grp.ret[k]?.n ?? 0) >= 5);
    if (h && grp.ret[h].avg != null) tip += ` · 이 등급 최근 ${grp.ret[h].n}건, +${h}일 평균 ${grp.ret[h].avg! >= 0 ? '+' : ''}${grp.ret[h].avg}%(실측)`;
  }
  return (
    <div className="flex flex-col items-start gap-0.5">
      {/* 등급과 원점수를 한 칩에 — 두 조각으로 흩어져 있던 걸 합쳤다(캔버스 '최상급 94') */}
      <span className={`${CHIP} gap-1 ${g.cls}`} title={tip}>{g.label} <span className="tabular-nums">{score}</span></span>
      {score >= 80 && chg <= -5 && (
        <span className="text-[10.5px] text-dc-warn whitespace-nowrap" title="종합점수는 어제까지 일봉 기준 — 오늘 급락분은 아직 미반영. 재평가 대기(점수가 고장난 게 아님).">오늘 급락</span>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 12 }).map((_, i) => (
        <tr key={i} className="border-b border-dc-hair/80">
          {Array.from({ length: 11 }).map((_, j) => (
            <td key={j} className="px-3 py-2.5">
              <div className="skeleton h-3 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

interface Props {
  data: ScannerItem[];
  loading: boolean;
  strategy?: string;   // minervini | reversion
  sigStats?: ScannerSignalStats | null;   // ② 시그널 신뢰도 꼬리표(성적표)
  onRowClick?: (item: ScannerItem) => void;
}

export default function ScannerTable({ data, loading, strategy = 'minervini', sigStats, onRowClick }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'score', desc: true }]);
  const isRev = strategy === 'reversion';

  const columns = useMemo<ColumnDef<ScannerItem>[]>(
    () => [
      {
        id: 'rank',
        header: '#',
        accessorKey: 'rank',
        cell: (info) => (
          <span className="text-dc-ink3 text-[11.5px]">{info.getValue<number>()}</span>
        ),
        size: 36,
        enableSorting: false,
      },
      {
        id: 'name',
        header: '종목명',
        accessorKey: 'name',
        cell: (info) => {
          const row = info.row.original;
          return (
            <div>
              <div className="text-dc-ink1 text-[13px] font-bold leading-tight">{row.name}</div>
              <div className="flex items-center gap-1">
                <span className="text-dc-ink3 text-[11.5px]">{row.ticker}</span>
                {row.earnings_dday != null && <EarningsBadge dday={row.earnings_dday} />}
                {row.ownership_flag && (
                  <span className={`${MINICHIP} ${row.ownership_flag === '자사주' ? TONE.upSoft : TONE.mute}`}
                    title={row.ownership_flag === '자사주' ? '최근 자사주 취득 공시 — 회사가 자기 주식을 삼(싸다고 판단한 신호)' : '최근 5%룰 대량보유 공시 — 큰손 지분 변동'}>
                    {row.ownership_flag}
                  </span>
                )}
              </div>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'sector',
        header: '분류',
        meta: { className: 'hidden md:table-cell' },
        accessorKey: 'sector',
        cell: (info) => {
          const at = info.row.original.asset_type;
          return (
            <div className="flex items-center gap-1 whitespace-nowrap">
              {at === 'ETF' && <span className={`${MINICHIP} shrink-0 ${TONE.mute}`}>ETF</span>}
              {at === '미국' && <span className={`${MINICHIP} shrink-0 ${TONE.mute}`}>US</span>}
              <span className="text-dc-ink2 text-xs whitespace-nowrap">{info.getValue<string>()}</span>
            </div>
          );
        },
        size: 130,
      },
      {
        id: 'score',
        header: isRev ? '역추세' : '등급',
        accessorFn: (row) => (isRev ? (row.reversion_score ?? 0) : row.score),
        cell: (info) => isRev
          ? <ScoreBar score={info.getValue<number>()} />
          : <GradeCell score={info.getValue<number>()} chg={info.row.original.price_change_pct} sigStats={sigStats} met={info.row.original.conditions_met} failed={info.row.original.conditions_failed} />,
        size: 120,
      },
      {
        id: 'rs_rank',
        header: '상대 힘',
        meta: { className: 'hidden md:table-cell' },
        accessorKey: 'rs_rank',
        cell: (info) => {
          const v = info.getValue<number | null>();
          if (v == null) return <span className="text-xs text-dc-ink3" title="신규 상장 — 상대 힘 산출 불가">─</span>;
          const top = 100 - v;   // 백분위 → 상위 %
          // 미니 막대 — 채움은 앰버(강함)/회색(그 밖). 규격 V2의 '달성 막대' 규칙과 같은 색.
          //   앰버 기준은 전 화면 공통 90+(등급 '최상급'과 같은 문턱) — 그 미만은 회색 사다리.
          const cls = v >= 80 ? 'text-dc-ink1' : v >= 40 ? 'text-dc-ink2' : 'text-dc-ink3';
          const fill = v >= 90 ? 'bg-dc-point' : v >= 40 ? 'bg-[#8E9AB0]' : 'bg-dc-ink3';
          return (
            <span className={`flex items-center gap-1.5 ${cls}`} title="전 종목(자산유형 풀) 중 수익률 순위 — 남들과 비교한 힘. 등급=이 종목 추세의 질 / 상대 힘=비교한 힘">
              <span className="text-xs whitespace-nowrap">상위 {top}%</span>
              <span className="inline-block w-11 h-[3px] rounded-full bg-dc-line1 overflow-hidden shrink-0"><span className={`block h-full rounded-full ${fill}`} style={{ width: `${v}%` }} /></span>
            </span>
          );
        },
        size: 84,
      },
      {
        id: 'signal',
        header: '시그널',
        accessorFn: (row) => (isRev ? (row.reversion_signal ?? '중립') : row.signal),
        cell: (info) => {
          const sig = info.getValue<string>();
          const cls = SIGNAL_STYLE[sig] ?? TONE.mute;
          // ④ⓑ 매우 강함 세분 — 가격이 추세의 어디에 있나(신고가부근/눌림/이탈)
          const fh = info.row.original.from_high_pct;
          const md = info.row.original.ma20_dist;
          let sub: { label: string; cls: string; tip: string } | null = null;
          if (!isRev && sig === '완벽한추세') {
            if (fh != null && fh >= -5) sub = { label: '신고가부근', cls: 'text-dc-point', tip: `52주 고점 대비 ${fh}% — 신고가 부근(주도주, 추격은 주의)` };
            else if (md != null && md < 0 && md >= -8) sub = { label: '눌림', cls: 'text-dc-ink2', tip: `MA20 대비 ${md}% — 추세 유효한 눌림(진입 후보 구간)` };
            else if (md != null && md < -8) sub = { label: '이탈', cls: 'text-dc-warn', tip: `MA20 아래 ${md}% — 단기 구조 이탈(주의)` };
          }
          return (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className={`${CHIP} ${cls}`}>{sigLabel(sig)}</span>
              {sub && (
                <span className={`text-[10.5px] ${sub.cls}`} title={sub.tip}>{sub.label}</span>
              )}
              {/* 🅓 신규진입 첫날 경고 — 포워드테스트 실측상 첫날 변동 크고 평균 부진. 추격 방지(숫자는 성적표에 라이브). */}
              {!isRev && sig === '신규진입' && (
                <span className="text-[10.5px] text-dc-warn" title="추세 초기는 첫날 변동이 큽니다(포워드테스트 실측 첫날 평균 마이너스). 추격 말고 눌림·지지 확인 후 분할 진입 권장.">⚠</span>
              )}
              {!isRev && info.row.original.short_term && (
                <span className={`${MINICHIP} ${TONE.faint}`} title="상장 1년 미만 — 단기 추세 기준 점수">
                  단기
                </span>
              )}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: 'current_price',
        header: '현재가',
        accessorKey: 'current_price',
        cell: (info) => (
          <span className="text-[13px] font-bold text-dc-ink1">
            {Number(info.getValue<number>()).toLocaleString('ko-KR')}
          </span>
        ),
      },
      {
        id: 'price_change_pct',
        header: '등락률',
        meta: { className: 'hidden sm:table-cell' },
        accessorKey: 'price_change_pct',
        cell: (info) => {
          const v = info.getValue<number>();
          const color = v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink3';
          const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '';
          return (
            <span className={`text-xs font-bold ${color}`}>
              {arrow}{Math.abs(v).toFixed(2)}%
            </span>
          );
        },
      },
      {
        id: 'rsi',
        header: 'RSI',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'rsi',
        cell: (info) => {
          const v = info.getValue<number>();
          const color = v >= 70 ? 'text-dc-up' : v <= 30 ? 'text-dc-down' : 'text-dc-ink2';
          return <span className={`text-xs ${color}`}>{v.toFixed(1)}</span>;
        },
      },
      {
        id: 'volume_ratio',
        header: '거래량비율',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'volume_ratio',
        cell: (info) => {
          const v = info.getValue<number>();
          // 평소의 2배 넘게 거래된 종목만 밝게 — 초록 강조는 은퇴(규격 V2)
          const cls = v >= 2.0 ? 'text-dc-ink1 font-bold' : 'text-dc-ink2';
          return <span className={`text-xs ${cls}`}>{v.toFixed(2)}x</span>;
        },
      },
      {
        id: 'ma_alignment',
        header: 'MA배열',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'ma_alignment',
        cell: (info) => {
          const v = info.getValue<string>();
          // 한국식 색 관례로 바로잡음 — 정배열(강세)=웜, 역배열(약세)=쿨. 옛 초록/빨강은 뜻이 뒤집혀 있었다.
          const color =
            v === '정배열' ? 'text-dc-up' : v === '역배열' ? 'text-dc-down' : 'text-dc-ink2';
          return <span className={`text-xs ${color}`}>{v}</span>;
        },
        enableSorting: false,
      },
    ],
    [isRev, sigStats]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-dc-hair">
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className={`px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap ${(header.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''}`}
                  style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                >
                  <span className={`flex items-center gap-1 ${header.column.getCanSort() ? 'cursor-pointer hover:text-dc-ink2' : ''}`}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' && <ChevronUp size={12} />}
                    {header.column.getIsSorted() === 'desc' && <ChevronDown size={12} />}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows />
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-dc-ink3 text-sm">
                데이터 없음
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-dc-hair/80 hover:bg-white/[0.015] cursor-pointer transition-colors"
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`px-2 sm:px-3 py-2.5 ${(cell.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
